// src/components/admin/settings/TermsSettings.jsx
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FileText, Plus, X, Languages } from 'lucide-react';
import { toast } from 'react-hot-toast';
import styles from '../../../styles/Settings.module.css';
import useTranslation from '../../../hooks/useTranslation';
import { getTermsOfService, updateTermsOfService, selectTermsOfService, selectSettingsUpdating } from '../../../store/slices/platformSettingsSlice';

const TermsSettings = () => {
  const dispatch = useDispatch();
  const termsOfServiceRedux = useSelector(selectTermsOfService);
  const isSaving = useSelector(selectSettingsUpdating);

  const { translatedText: termsOfService } = useTranslation('Terms of Service');
  const { translatedText: platformName } = useTranslation('Platform Name');
  const { translatedText: version } = useTranslation('Version');
  const { translatedText: active } = useTranslation('Active');
  const { translatedText: requiresAcceptance } = useTranslation('Requires Acceptance');
  const { translatedText: acceptanceRequiredFor } = useTranslation('Acceptance Required For');
  const { translatedText: allUsers } = useTranslation('All Users');
  const { translatedText: providersOnly } = useTranslation('Providers Only');
  const { translatedText: clientsOnly } = useTranslation('Clients Only');
  const { translatedText: changesSummary } = useTranslation('Changes Summary');
  const { translatedText: termsContent } = useTranslation('Terms Content');
  const { translatedText: addSection } = useTranslation('Add Section');
  const { translatedText: sectionTitle } = useTranslation('Section Title');
  const { translatedText: sectionContent } = useTranslation('Section Content');
  const { translatedText: removeSection } = useTranslation('Remove Section');
  const { translatedText: translations } = useTranslation('Translations');
  const { translatedText: saveChanges } = useTranslation('Save Changes');
  const { translatedText: savingText } = useTranslation('Saving...');

  const [termsData, setTermsData] = useState({
    title: '',
    version: '1.0.0',
    isActive: true,
    requiresAcceptance: true,
    acceptanceRequiredFor: 'all',
    sections: [],
    translations: {
      fr: { title: '', sections: [] },
      ht: { title: '', sections: [] }
    },
    changesSummary: ''
  });

  useEffect(() => {
    dispatch(getTermsOfService());
  }, [dispatch]);

  useEffect(() => {
    if (termsOfServiceRedux) {
      setTermsData({
        title: termsOfServiceRedux.title || 'Terms of Service',
        version: termsOfServiceRedux.version || '1.0.0',
        isActive: termsOfServiceRedux.isActive !== undefined ? termsOfServiceRedux.isActive : true,
        requiresAcceptance: termsOfServiceRedux.requiresAcceptance !== undefined ? termsOfServiceRedux.requiresAcceptance : true,
        acceptanceRequiredFor: termsOfServiceRedux.acceptanceRequiredFor || 'all',
        sections: termsOfServiceRedux.content?.sections || [],
        translations: termsOfServiceRedux.translations || {
          fr: { title: '', sections: [] },
          ht: { title: '', sections: [] }
        },
        changesSummary: ''
      });
    }
  }, [termsOfServiceRedux]);

  // Section management
  const addSectionHandler = () => {
    setTermsData(prev => ({
      ...prev,
      sections: [
        ...prev.sections,
        { title: `Section ${prev.sections.length + 1}`, content: '', order: prev.sections.length + 1 }
      ]
    }));
  };

  const removeSectionHandler = (index) => {
    setTermsData(prev => ({
      ...prev,
      sections: prev.sections.filter((_, i) => i !== index)
    }));
  };

  const updateSection = (index, field, value) => {
    setTermsData(prev => ({
      ...prev,
      sections: prev.sections.map((section, i) =>
        i === index ? { ...section, [field]: value } : section
      )
    }));
  };

  // Translation handlers
  const updateTranslation = (lang, field, value) => {
    setTermsData(prev => ({
      ...prev,
      translations: {
        ...prev.translations,
        [lang]: {
          ...prev.translations[lang],
          [field]: value
        }
      }
    }));
  };

  const updateTranslationSection = (lang, index, field, value) => {
    setTermsData(prev => ({
      ...prev,
      translations: {
        ...prev.translations,
        [lang]: {
          ...prev.translations[lang],
          sections: prev.translations[lang].sections.map((section, i) =>
            i === index ? { ...section, [field]: value } : section
          )
        }
      }
    }));
  };

  const addTranslationSection = (lang) => {
    setTermsData(prev => ({
      ...prev,
      translations: {
        ...prev.translations,
        [lang]: {
          ...prev.translations[lang],
          sections: [
            ...prev.translations[lang].sections,
            { title: `Section ${prev.translations[lang].sections.length + 1}`, content: '', order: prev.translations[lang].sections.length + 1 }
          ]
        }
      }
    }));
  };

  const removeTranslationSection = (lang, index) => {
    setTermsData(prev => ({
      ...prev,
      translations: {
        ...prev.translations,
        [lang]: {
          ...prev.translations[lang],
          sections: prev.translations[lang].sections.filter((_, i) => i !== index)
        }
      }
    }));
  };

  const handleSave = async () => {
    try {
      const updateData = {
        title: termsData.title,
        version: termsData.version,
        isActive: termsData.isActive,
        requiresAcceptance: termsData.requiresAcceptance,
        acceptanceRequiredFor: termsData.acceptanceRequiredFor,
        content: { sections: termsData.sections },
        translations: termsData.translations,
        changesSummary: termsData.changesSummary || 'Updated Terms of Service'
      };
      
      await dispatch(updateTermsOfService(updateData)).unwrap();
      toast.success('Terms of Service updated successfully!');
      dispatch(getTermsOfService());
    } catch (error) {
      toast.error(error || 'Failed to update Terms of Service');
    }
  };

  return (
    <div className={styles.settingsCard}>
      <h3>
        <FileText size={20} />
        {termsOfService || 'Terms of Service'}
      </h3>
      
      <div className={styles.settingsForm}>
        {/* Basic Info */}
        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label>{platformName || 'Title'}</label>
            <input
              type="text"
              value={termsData.title}
              onChange={(e) => setTermsData({ ...termsData, title: e.target.value })}
              placeholder="Terms of Service"
            />
          </div>
          <div className={styles.formGroup}>
            <label>{version || 'Version'}</label>
            <input
              type="text"
              value={termsData.version}
              onChange={(e) => setTermsData({ ...termsData, version: e.target.value })}
              placeholder="1.0.0"
            />
          </div>
        </div>

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label className={styles.toggleLabel}>
              <input
                type="checkbox"
                checked={termsData.isActive}
                onChange={(e) => setTermsData({ ...termsData, isActive: e.target.checked })}
              />
              {active || 'Active'}
            </label>
          </div>
          <div className={styles.formGroup}>
            <label className={styles.toggleLabel}>
              <input
                type="checkbox"
                checked={termsData.requiresAcceptance}
                onChange={(e) => setTermsData({ ...termsData, requiresAcceptance: e.target.checked })}
              />
              {requiresAcceptance || 'Requires Acceptance'}
            </label>
          </div>
          <div className={styles.formGroup}>
            <label>{acceptanceRequiredFor || 'Acceptance Required For'}</label>
            <select
              value={termsData.acceptanceRequiredFor}
              onChange={(e) => setTermsData({ ...termsData, acceptanceRequiredFor: e.target.value })}
            >
              <option value="all">{allUsers || 'All Users'}</option>
              <option value="providers_only">{providersOnly || 'Providers Only'}</option>
              <option value="clients_only">{clientsOnly || 'Clients Only'}</option>
            </select>
          </div>
        </div>

        <div className={styles.formGroup}>
          <label>{changesSummary || 'Changes Summary'}</label>
          <input
            type="text"
            value={termsData.changesSummary}
            onChange={(e) => setTermsData({ ...termsData, changesSummary: e.target.value })}
            placeholder="Summary of changes made"
          />
        </div>

        {/* Sections */}
        <div className={styles.sectionsHeader}>
          <h4>{termsContent || 'Terms Content'}</h4>
          <button className={styles.addSectionBtn} onClick={addSectionHandler}>
            <Plus size={16} />
            {addSection || 'Add Section'}
          </button>
        </div>

        {termsData.sections.map((section, index) => (
          <div key={index} className={styles.sectionItem}>
            <div className={styles.sectionHeader}>
              <span>Section {index + 1}</span>
              <button 
                className={styles.removeSectionBtn}
                onClick={() => removeSectionHandler(index)}
              >
                <X size={16} />
              </button>
            </div>
            <div className={styles.formGroup}>
              <label>{sectionTitle || 'Section Title'}</label>
              <input
                type="text"
                value={section.title}
                onChange={(e) => updateSection(index, 'title', e.target.value)}
                placeholder="Enter section title"
              />
            </div>
            <div className={styles.formGroup}>
              <label>{sectionContent || 'Section Content'}</label>
              <textarea
                value={section.content}
                onChange={(e) => updateSection(index, 'content', e.target.value)}
                rows={3}
                placeholder="Enter section content"
              />
            </div>
          </div>
        ))}

        {/* Translations */}
        <h4 className={styles.subsectionTitle}>
          <Languages size={16} />
          {translations || 'Translations'}
        </h4>

        {/* French Translation */}
        <div className={styles.translationSection}>
          <h5>🇫🇷 Français</h5>
          <div className={styles.formGroup}>
            <label>{platformName || 'Title'}</label>
            <input
              type="text"
              value={termsData.translations.fr.title}
              onChange={(e) => updateTranslation('fr', 'title', e.target.value)}
              placeholder="Conditions d'Utilisation"
            />
          </div>
          <button 
            className={styles.addSectionBtn}
            onClick={() => addTranslationSection('fr')}
          >
            <Plus size={14} />
            {addSection || 'Add Section'}
          </button>
          {termsData.translations.fr.sections.map((section, index) => (
            <div key={index} className={styles.translationSectionItem}>
              <div className={styles.sectionHeader}>
                <span>Section {index + 1}</span>
                <button 
                  className={styles.removeSectionBtn}
                  onClick={() => removeTranslationSection('fr', index)}
                >
                  <X size={14} />
                </button>
              </div>
              <div className={styles.formGroup}>
                <label>{sectionTitle || 'Section Title'}</label>
                <input
                  type="text"
                  value={section.title}
                  onChange={(e) => updateTranslationSection('fr', index, 'title', e.target.value)}
                  placeholder="Titre de la section"
                />
              </div>
              <div className={styles.formGroup}>
                <label>{sectionContent || 'Section Content'}</label>
                <textarea
                  value={section.content}
                  onChange={(e) => updateTranslationSection('fr', index, 'content', e.target.value)}
                  rows={2}
                  placeholder="Contenu de la section"
                />
              </div>
            </div>
          ))}
        </div>

        {/* Haitian Creole Translation */}
        <div className={styles.translationSection}>
          <h5>🇭🇹 Kreyòl</h5>
          <div className={styles.formGroup}>
            <label>{platformName || 'Title'}</label>
            <input
              type="text"
              value={termsData.translations.ht.title}
              onChange={(e) => updateTranslation('ht', 'title', e.target.value)}
              placeholder="Kondisyon Sèvis"
            />
          </div>
          <button 
            className={styles.addSectionBtn}
            onClick={() => addTranslationSection('ht')}
          >
            <Plus size={14} />
            {addSection || 'Add Section'}
          </button>
          {termsData.translations.ht.sections.map((section, index) => (
            <div key={index} className={styles.translationSectionItem}>
              <div className={styles.sectionHeader}>
                <span>Section {index + 1}</span>
                <button 
                  className={styles.removeSectionBtn}
                  onClick={() => removeTranslationSection('ht', index)}
                >
                  <X size={14} />
                </button>
              </div>
              <div className={styles.formGroup}>
                <label>{sectionTitle || 'Section Title'}</label>
                <input
                  type="text"
                  value={section.title}
                  onChange={(e) => updateTranslationSection('ht', index, 'title', e.target.value)}
                  placeholder="Tit Seksyon"
                />
              </div>
              <div className={styles.formGroup}>
                <label>{sectionContent || 'Section Content'}</label>
                <textarea
                  value={section.content}
                  onChange={(e) => updateTranslationSection('ht', index, 'content', e.target.value)}
                  rows={2}
                  placeholder="Kontni Seksyon"
                />
              </div>
            </div>
          ))}
        </div>

        <button 
          className={styles.saveBtn} 
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? (savingText || 'Saving...') : (saveChanges || 'Save Changes')}
        </button>
      </div>
    </div>
  );
};

export default TermsSettings;